import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  Avatar,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import { getImageDisplayUrl } from "src/network/endpoints";

const TeamMembersDialog = ({
  open,
  onClose,
  category,
  teamMemberList,
  onSave,
  chatCategories,
  categoryIndex,
  selectedLocationId,
}) => {
  // local state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);

  // process real team member data and filter by selected location
  const processedTeamMembers = useMemo(() => {
    if (!teamMemberList?.employees) return [];

    return teamMemberList.employees
      .filter((employee) => {
        // Filter out employees with null userId
        if (!employee.userId) return false;
        
        // If location is selected, filter employees assigned to that location
        if (selectedLocationId) {
          const locationIds = employee.locationIds || [];
          return locationIds.includes(selectedLocationId);
        }
        
        // If no location selected, show all employees
        return true;
      })
      .map((employee) => ({
        id: employee.userId._id,
        name: employee.userId?.fullName || "unknown",
        role: employee.designation || "no role",
        department: employee.department || "no department",
        type: "member",
        avatar: employee.userId?.image?.profilePic || null,
        email: employee.userId?.email,
        mobile: employee.userId?.mobile,
        isOwner: employee.isOwner || false,
        status: employee.status,
        locationIds: employee.locationIds || [],
      }));
  }, [teamMemberList, selectedLocationId]);

  // group team members by department
  const departmentsWithMembers = useMemo(() => {
    if (!processedTeamMembers.length) return [];

    const departmentMap = {};
    processedTeamMembers.forEach((member) => {
      const dept = member.department || "no department";
      if (!departmentMap[dept]) {
        departmentMap[dept] = {
          id: dept,
          name: dept,
          type: "department",
          avatar: "🏢",
          members: [],
          memberCount: 0,
        };
      }
      departmentMap[dept].members.push(member);
      departmentMap[dept].memberCount += 1;
    });

    return Object.values(departmentMap);
  }, [processedTeamMembers]);

  // initialize selections when dialog opens
  React.useEffect(() => {
    if (open && category) {
      setSelectedDepartments(category.department || []);
      setSelectedTeamMembers(category.teamMemberId || []);
      setSearchQuery("");
    }
  }, [open, category]);

  // handlers
  const handleDepartmentToggle = (departmentName) => {
    setSelectedDepartments((prev) => {
      if (prev.includes(departmentName)) {
        // remove department and all its members
        const updatedDepartments = prev.filter(
          (dept) => dept !== departmentName
        );
        const membersToRemove = processedTeamMembers
          .filter((member) => member.department === departmentName)
          .map((member) => member.id);

        setSelectedTeamMembers((prevMembers) =>
          prevMembers.filter((id) => !membersToRemove.includes(id))
        );

        return updatedDepartments;
      } else {
        // add department and all its members
        const membersToAdd = processedTeamMembers
          .filter((member) => member.department === departmentName)
          .map((member) => member.id);

        setSelectedTeamMembers((prevMembers) => {
          const newMembers = [...prevMembers];
          membersToAdd.forEach((memberId) => {
            if (!newMembers.includes(memberId)) {
              newMembers.push(memberId);
            }
          });
          return newMembers;
        });

        return [...prev, departmentName];
      }
    });
  };

  const handleTeamMemberToggle = (memberId) => {
    const member = processedTeamMembers.find((m) => m.id === memberId);
    if (!member) return;

    setSelectedTeamMembers((prev) => {
      if (prev.includes(memberId)) {
        // remove member
        const updatedMembers = prev.filter((id) => id !== memberId);

        // check if we should remove the department
        const remainingMembersInDept = updatedMembers.filter((id) => {
          const m = processedTeamMembers.find((pm) => pm.id === id);
          return m && m.department === member.department;
        });

        if (remainingMembersInDept.length === 0) {
          // remove department if no members are selected from it
          setSelectedDepartments((prevDepts) =>
            prevDepts.filter((dept) => dept !== member.department)
          );
        }

        return updatedMembers;
      } else {
        // add member
        const updatedMembers = [...prev, memberId];

        // check if we should add the department
        const allMembersInDept = processedTeamMembers.filter(
          (m) => m.department === member.department
        );
        const selectedMembersInDept = updatedMembers.filter((id) => {
          const m = processedTeamMembers.find((pm) => pm.id === id);
          return m && m.department === member.department;
        });

        if (selectedMembersInDept.length === allMembersInDept.length) {
          // add department if all members from it are selected
          setSelectedDepartments((prevDepts) => {
            if (!prevDepts.includes(member.department)) {
              return [...prevDepts, member.department];
            }
            return prevDepts;
          });
        }

        return updatedMembers;
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // select all departments and team members
      setSelectedDepartments(departmentsWithMembers.map((dept) => dept.name));
      setSelectedTeamMembers(processedTeamMembers.map((member) => member.id));
    } else {
      // deselect all
      setSelectedDepartments([]);
      setSelectedTeamMembers([]);
    }
  };

  const handleSave = () => {
    if (categoryIndex >= 0) {
      const updatedCategories = [...chatCategories];
      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        department: selectedDepartments,
        teamMemberId: selectedTeamMembers,
      };

      onSave(updatedCategories);
      onClose();
      // toast.success("team members updated  ");
    }
  };

  // helper function to check if department should be visually selected
  const isDepartmentVisuallySelected = (departmentName) => {
    const membersInDept = processedTeamMembers.filter(
      (member) => member.department === departmentName
    );
    const selectedMembersInDept = selectedTeamMembers.filter((id) => {
      const member = processedTeamMembers.find((m) => m.id === id);
      return member && member.department === departmentName;
    });

    // department is visually selected if all its members are selected
    return (
      membersInDept.length > 0 &&
      selectedMembersInDept.length === membersInDept.length
    );
  };

  // filter data based on search query
  const filteredDepartments = useMemo(() => {
    if (!searchQuery) return departmentsWithMembers;
    return departmentsWithMembers.filter((dept) =>
      dept.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [departmentsWithMembers, searchQuery]);

  const filteredTeamMembers = useMemo(() => {
    if (!searchQuery) return processedTeamMembers;
    return processedTeamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.department.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [processedTeamMembers, searchQuery]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
          height: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#7c28d6",
          fontWeight: 700,
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          manage team members & departments - {category?.name || ""}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#666" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Fixed Header Section */}
        <Box sx={{ flexShrink: 0 }}>
          {/* Search Bar */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <TextField
              fullWidth
              placeholder="search team members and departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "#999", mr: 1 }} />,
                sx: {
                  borderRadius: 2,
                  bgcolor: "#f7f2fa",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                },
              }}
            />
          </Box>

          {/* Selection Summary */}
          <Box sx={{ mb: 2, p: 2, bgcolor: "#f7f2fa", borderRadius: 2 }}>
            <Typography sx={{ color: "#7c28d6", fontWeight: 600, mb: 1 }}>
              current selections:
            </Typography>
            <Typography sx={{ color: "#666", fontSize: "0.9em" }}>
              members selected ({selectedTeamMembers.length}) • department
              selected ({selectedDepartments.length})
            </Typography>
          </Box>

          {/* Add Options */}
          {/* <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              sx={{
                color: "#FFF",
                borderColor: "#7c28d6",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#5a1b8a",
                  bgcolor: "#f3e5f5",
                },
              }}
            >
              +add team member
            </Button>
            <Button
              variant="outlined"
              sx={{
                color: "#FFF",
                borderColor: "#7c28d6",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#5a1b8a",
                  bgcolor: "#f3e5f5",
                },
              }}
            >
              +add department
            </Button>
          </Box> */}

          {/* Default All Checkbox */}
          <Box sx={{ mb: 2 }}>
            <Checkbox
              checked={
                selectedDepartments.length === departmentsWithMembers.length &&
                selectedTeamMembers.length === processedTeamMembers.length &&
                departmentsWithMembers.length > 0 &&
                processedTeamMembers.length > 0
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
              sx={{
                color: "#7c28d6",
                "&.Mui-checked": {
                  color: "#7c28d6",
                },
              }}
            />
            <Typography
              component="span"
              sx={{ color: "#333", fontWeight: 500, ml: 1 }}
            >
              default (all)
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />
        </Box>

        {/* Scrollable Content Section */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            pr: 1,
            py: 1,
          }}
        >
          {!teamMemberList ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ color: "#666", fontSize: "0.9em" }}>
                loading team members...
              </Typography>
            </Box>
          ) : processedTeamMembers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ color: "#666", fontSize: "0.9em" }}>
                no team members found
              </Typography>
            </Box>
          ) : (
            <>
              {/* Departments */}
              {/* {filteredDepartments.map((dept) => (
                <Box
                  key={dept.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    mb: 1,
                    bgcolor: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <Checkbox
                    checked={isDepartmentVisuallySelected(dept.name)}
                    onChange={() => handleDepartmentToggle(dept.name)}
                    sx={{
                      color: "#7c28d6",
                      "&.Mui-checked": {
                        color: "#7c28d6",
                      },
                    }}
                  />
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "#e0eafc",
                      color: "#2b63e3",
                      fontSize: "1.2em",
                      mr: 2,
                    }}
                  >
                    {dept.avatar}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: "#333" }}>
                      {dept.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8em", color: "#666" }}>
                      {dept.memberCount} members
                    </Typography>
                  </Box>
                  <IconButton
                    sx={{
                      color: "#666",
                      "&:hover": { color: "#d32f2f", bgcolor: "#ffebee" },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))} */}

              {/* Individual Team Members */}
              {filteredTeamMembers.map((member) => (
                <Box
                  key={member.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    mb: 1,
                    bgcolor: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <Checkbox
                    checked={selectedTeamMembers.includes(member.id)}
                    onChange={() => handleTeamMemberToggle(member.id)}
                    sx={{
                      color: "#7c28d6",
                      "&.Mui-checked": {
                        color: "#7c28d6",
                      },
                    }}
                  />
                  <Avatar
                    src={
                      member.avatar
                        ? getImageDisplayUrl(member.avatar, `${process.env.REACT_APP_API_URL || ""}/uploads/`)
                        : undefined
                    }
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: member.avatar ? "transparent" : "#eaf7fa",
                      color: "#135b76",
                      fontSize: "1.2em",
                      mr: 2,
                    }}
                  >
                    {!member.avatar && (member.name?.charAt(0) || "👤")}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: "#333" }}>
                      {member.name}
                      {member.isOwner && (
                        <Chip
                          size="small"
                          label="owner"
                          sx={{
                            ml: 1,
                            height: 18,
                            fontSize: "0.6em",
                            bgcolor: "#7c28d6",
                            color: "#fff",
                          }}
                        />
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8em", color: "#666" }}>
                      {member.role} • {member.department}
                    </Typography>
                    {member.email && (
                      <Typography sx={{ fontSize: "0.7em", color: "#999" }}>
                        {member.email}
                      </Typography>
                    )}
                  </Box>
                  {/* <IconButton
                    sx={{
                      color: "#666",
                      "&:hover": { color: "#d32f2f", bgcolor: "#ffebee" },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton> */}
                </Box>
              ))}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#FFF",
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            bgcolor: "#7c28d6",
            "&:hover": { bgcolor: "#5a1b8a" },
            textTransform: "none",
            fontWeight: 500,
            px: 3,
          }}
        >
          save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamMembersDialog;
